import { BUILD_LIMITS, SUBMISSION_LIMITS } from '@mogzo/shared';
import { CodeBlock, Notice } from '@mogzo/ui';
import { Code, DocLink, Lead } from '../parts';

const MB = 1024 * 1024;

export function Splash() {
  return (
    <>
      <h1>Splash and launch</h1>
      <Lead>
        In posts, MOGZO shows your game through its splash: a small page of your own that players
        see before they press Play.
      </Lead>

      <h2 id="splash">The splash</h2>
      <p>
        Name it with <Code>splash</Code> in <DocLink page="manifest">mogzo.json</DocLink>. It can
        show anything: the game playing itself, this week’s top score, a challenge. It runs in the
        same locked-down frame as the game and can use the SDK the same way. Without a splash, MOGZO
        shows your game’s name and a Play button.
      </p>

      <h2 id="launch">Launch the game</h2>
      <CodeBlock
        title="splash.js and game.js"
        code={`// In the splash, from a Play button's click:
playButton.addEventListener('click', () => mogzo.launch({ mode: 'daily' }));

// In the game:
mogzo.view();          // 'game' (and 'splash' in the splash)
mogzo.launchParams();  // { mode: 'daily' }
mogzo.onPause(() => pauseTheGame());   // MOGZO's pause button on a phone
mogzo.onResume(() => carryOn());`}
      />
      <p>
        The game opens full screen on a phone, on its play page on a computer. Launch params are up
        to 10 short texts.
      </p>

      <h2 id="rules">Rules for a good splash</h2>
      <ul>
        <li>
          A splash runs only while its post is near the screen, and several can run at once, so keep
          it light: the CLI warns above {BUILD_LIMITS.splashBytes / MB} MB with what it loads.
        </li>
        <li>
          Posts show it wide (16:9) on computers and tall (4:5) on phones. Fill the frame without
          scrolling it.
        </li>
        <li>
          A splash can be played right in the post, like a daily puzzle. But on a phone a swipe that
          starts on it must still scroll the feed, so don’t put <Code>touch-action: none</Code> or{' '}
          <Code>overscroll-behavior: none</Code> on its <Code>html</Code>, <Code>body</Code> or a
          canvas that fills it; turn swipes off only on the piece the player drags.
        </li>
        <li>
          Launch from a click on a button, never when a touch starts: on a phone that’s also how a
          swipe through the feed starts. MOGZO ignores a launch that doesn’t come from a tap or
          click.
        </li>
      </ul>
    </>
  );
}

export function Panels() {
  return (
    <>
      <h1>MOGZO’s panels and Game UI</h1>
      <Lead>
        MOGZO draws its own panels over your game: the achievement pop-up, the leaderboard, the
        achievements list, the pause menu and a sign-in prompt for guests.
      </Lead>

      <h2 id="open">Open a panel</h2>
      <CodeBlock
        title="game.js"
        code={`await mogzo.ui.showLeaderboard('weekly-best');   // or { view: 'around', period: 'previous' }
await mogzo.ui.showAchievements();
await mogzo.ui.showPauseMenu();                   // from your own pause key or button
await mogzo.ui.promptSignIn();                    // guests only, such as after a new best`}
      />
      <ul>
        <li>
          Each call answers when the player closes the panel. Your game gets <Code>onPause</Code>{' '}
          while it’s open and <Code>onResume</Code> after.
        </li>
        <li>
          The pop-up shows by itself on every unlock, one after another; players can turn pop-ups
          off in the pause menu.
        </li>
        <li>
          On a computer the player bar has a pause button, and Esc opens the menu while the page has
          the keyboard; while your game has it, call <Code>showPauseMenu()</Code> from your own
          pause key.
        </li>
      </ul>

      <h2 id="sound">Sound</h2>
      <CodeBlock
        title="game.js"
        code={`mogzo.settings();                                 // { sound: true }
mogzo.onSettings(({ sound }) => setSound(sound));`}
      />
      <p>
        The pause menu has a Sound switch. MOGZO can’t mute your game from outside, so play sound
        only while <Code>mogzo.settings().sound</Code> is true.
      </p>

      <h2 id="game-ui">How the panels look: the Game UI editor</h2>
      <p>
        Open it with the <strong>Game UI</strong> button in your project’s header; it opens in a new
        tab. Design the panels for your game: colours, font, corners, sizes and where the pop-up
        shows. Each screen size (computer, tablet and phone) can have its own values, for the splash
        and for the game, and each panel can change its own. Testers get a saved look straight away;
        live players with your next published version.
      </p>
    </>
  );
}

export function StoreListing() {
  return (
    <>
      <h1>Store listing</h1>
      <Lead>How your game shows on MOGZO: its game page, and its card in lists and search.</Lead>

      <h2 id="fill">Fill it in</h2>
      <p>
        On your project’s <strong>Store listing</strong> tab. It saves unfinished, and its Ready to
        submit list shows what’s still needed. Preview game page shows it as players will see it, on
        a computer and a phone.
      </p>
      <ul>
        <li>
          Title, the developer name players see (“by …”, such as your studio’s; it doesn’t have to
          be your own), a short description for cards (80 characters) and About the game (plain
          text, 4,000). One genre and up to 5 tags from MOGZO’s list.
        </li>
        <li>
          How to play: headings, lists, bold, keys (<Code>Ctrl</Code> or <Code>⌘</Code> +{' '}
          <Code>K</Code>), tips and up to 6 pictures. Write for computers and phones; both see the
          same text.
        </li>
        <li>
          Content rating: five questions give Everyone, 16+ or 18+. Reviewers check the answers
          against the game.
        </li>
      </ul>

      <h2 id="pictures">Pictures</h2>
      <ul>
        <li>PNG, JPEG or WebP: a square icon, 512 to 2,048 px, up to 1 MB.</li>
        <li>
          A 16 : 9 cover, at least 1920 × 1080, up to 4 MB. On a computer the game page shows the
          cover’s middle band, so keep words inside it.
        </li>
        <li>
          3 to 8 screenshots, at least 1280 px on the long side, up to 4 MB; wide or tall, each
          keeps its shape. A YouTube or Vimeo trailer is optional and comes first.
        </li>
        <li>
          MOGZO saves a fresh copy of each picture and removes hidden details, such as where a photo
          was taken.
        </li>
      </ul>

      <h2 id="from-the-cli">From the CLI and AI tools</h2>
      <p>
        You, or an AI coding tool in the game’s folder, can fill in the listing as files.{' '}
        <Code>mogzo listing pull</Code> writes the draft into <Code>listing/</Code>:{' '}
        <Code>listing.json</Code> with every field and the pictures beside it, and{' '}
        <Code>how-to-play.md</Code>. Edit them, then <Code>mogzo listing push</Code> shows what will
        change and makes it the draft. On this tab, the fields it changed are marked{' '}
        <strong>CLI</strong> until you change them here.
      </p>
      <ul>
        <li>
          Same rules as this tab: the limits, the picture checks, no links, and no words MOGZO
          doesn’t allow. Every problem is listed at once, with the file and line.
        </li>
        <li>
          How to play is written in a small part of Markdown: <Code>##</Code> and <Code>###</Code>{' '}
          headings, <Code>-</Code> and <Code>1.</Code> lists, <Code>&gt;</Code> for a tip,{' '}
          <Code>**bold**</Code>, <Code>*italic*</Code>, <Code>[[Space]]</Code> for a key and{' '}
          <Code>![caption](picture.png)</Code> on a line of its own.
        </li>
        <li>
          If you change the draft here after a pull, a push keeps your changes to other fields and
          stops when the same field changed in both. <Code>mogzo listing pull</Code> gets the new
          draft; <Code>--replace</Code> overwrites it.
        </li>
        <li>
          <Code>mogzo listing check</Code> shows the Ready to submit list for the files.
        </li>
        <li>
          <Code>mogzo screenshots</Code> plays the game in your own Chrome or Edge, without a
          window, and saves screenshots at the listing’s sizes in{' '}
          <Code>listing/screenshots/new/</Code>. <Code>--at 4s,12s,30s</Code> picks the moments;{' '}
          <Code>--params</Code> launches the game with params, such as a demo that plays itself.
          Move the ones you like into <Code>listing/screenshots/</Code>.
        </li>
        <li>
          The <Code>listing/</Code> folder isn’t part of your build. It goes in the source zip like
          your other files.
        </li>
      </ul>
      <CodeBlock
        title="Terminal"
        plain
        code={`mogzo listing pull
# edit listing/listing.json and listing/how-to-play.md
mogzo screenshots --at 4s,12s,30s
mogzo listing check
mogzo listing push`}
      />

      <h2 id="review">Review</h2>
      <p>
        Reviewers see the listing with each submission. Players see the one that went live with your
        live version; edits stay a draft until you send them. Once the game is live, you can send a
        listing change on its own (see{' '}
        <DocLink page="submitting" heading="listing">
          Submitting and publishing
        </DocLink>
        ).
      </p>
    </>
  );
}

export function Testing() {
  return (
    <>
      <h1>Testing</h1>
      <Lead>
        Every project has a private test community where your builds are posted for the testers you
        invite. Nothing there reaches real players.
      </Lead>

      <h2 id="community">Your test community</h2>
      <p>
        It’s at <Code>mogzo.com/test/&lt;address&gt;</Code>. Each <Code>mogzo upload</Code> posts
        the build there with its splash, pinned on top. Testers play it, post bugs, ideas and
        questions, comment and GG. Strangers get the same “not found” page as a missing one.
      </p>

      <h2 id="invite">Invite testers</h2>
      <p>
        On your project’s Overview, under Testers, make an invite link: it works for 1, 7 or 30
        days, for up to 1, 10 or 50 people, and you can turn it off. People without an account sign
        up from the link and come back to it.
      </p>
      <Notice tone="info" title="Testers must be 18 or older">
        MOGZO checks the age on each account; younger players can’t join a test community.
      </Notice>

      <h2 id="data">Test data stays apart</h2>
      <p>
        Saves, game data, scores and achievements in the test community are kept apart from your
        published game’s. See and change them on the project’s <strong>Data</strong> tab, with Test
        selected. New leaderboards, achievements and Game UI looks reach testers at once; live
        players get them with your next published version.
      </p>

      <h2 id="local">On your own computer</h2>
      <p>
        <Code>mogzo dev</Code> plays the game in a stand-in for mogzo.com, with a test player whose
        saves and game data stay in your browser. Signed in, it uses your test leaderboards and
        achievements. See{' '}
        <DocLink page="first-game" heading="play">
          Your first game
        </DocLink>
        .
      </p>
    </>
  );
}

export function Submitting() {
  return (
    <>
      <h1>Submitting and publishing</h1>
      <Lead>
        When the game and its store listing are ready, send it for review. Once it’s approved, you
        publish it.
      </Lead>

      <h2 id="send">Send a version</h2>
      <p>From the game’s folder:</p>
      <CodeBlock
        title="Terminal"
        plain
        code={'mogzo submit --notes "What the reviewer should know"'}
      />
      <p>
        It sends your newest build (or <Code>--build 12</Code>), a zip of your source and the
        listing as it is now, then waits for MOGZO’s checks.
      </p>
      <ul>
        <li>
          The version in <Code>mogzo.json</Code> must be higher than the last approved one, and
          match your newest build. One version can be in review at a time.
        </li>
        <li>
          It sends a build already on MOGZO, so run <Code>mogzo upload</Code> first after any
          change. If files changed since that build, it says which and asks before sending.
        </li>
        <li>
          The source zip leaves out <Code>node_modules</Code>, <Code>.git</Code>, <Code>.env</Code>{' '}
          files and your build’s output folder. Leave out anything else reviewers don’t need with a{' '}
          <Code>.mogzoignore</Code> file, written like a <Code>.gitignore</Code>. Up to{' '}
          {SUBMISSION_LIMITS.sourceBytes / MB} MB; only reviewers see it.
        </li>
      </ul>

      <h2 id="checks">The checks</h2>
      <p>
        <Code>mogzo.json</Code>, the store listing, size, a virus scan of the build and source, a
        code scan, network use, known security problems in your packages (from your lockfile), keys
        in the source, and the leaderboards and achievements that go live with it.
      </p>
      <p>
        A version fails, and you get an email, when the checks find a virus; scripts, requests or
        files from sites not in <Code>network.allow</Code>; crypto-mining; code that moves the page
        around the game; or keys and <Code>.env</Code> files in the source. Things that are often
        fine in bundled libraries, such as <Code>eval</Code>, are warnings: the reviewer looks at
        them, and so can you.
      </p>

      <h2 id="review">Review</h2>
      <p>
        A reviewer plays it on a computer and a phone, reads the source and checks the listing. Your
        project’s <strong>Submissions</strong> tab shows where it is, the full report, and lets you
        withdraw it.
      </p>

      <h2 id="publish">Publish</h2>
      <p>
        Once it’s approved, publish it from the <strong>Submissions</strong> tab, now or at a set
        time. It goes live with the store listing, leaderboards and achievements sent with it; live
        players’ data stays.
      </p>

      <h2 id="listing">A listing change on its own</h2>
      <p>
        Once the game is live, you can change the store listing with no new build: save it, then{' '}
        <strong>Submit listing for review</strong> on the Store listing tab, or{' '}
        <Code>mogzo submit --listing</Code>. A reviewer checks the listing and its rating, then you
        publish it the same way.
      </p>
    </>
  );
}
